import React from 'react';
import PropTypes from 'prop-types';
import { Steps, Button, Menu, Dropdown, Modal } from 'antd';
import { tldArray } from '../constants/tlds';
import {
  EllipsisOutlined,
  SyncOutlined,
  DeleteOutlined,
  LoadingOutlined,
  SettingOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

const { Step } = Steps;

const pageHitsIP = '35.235.69.127';
const pageHitsAlias = 'hosted.pagehits.io';

export default class DomainItem extends React.Component {
  static propTypes = {
    urlsafe: PropTypes.string,
    url: PropTypes.string,
    stage: PropTypes.string,
    status: PropTypes.string,

    deleteDomain: PropTypes.func.isRequired,
    checkDomainReachability: PropTypes.func.isRequired,
    refreshDomain: PropTypes.func.isRequired,
  };

  onCheckDomainReachabilityBtnClick = () => {
    this.props.checkDomainReachability(this.props.urlsafe);
  };

  removeAfterConfirm = () => {
    Modal.confirm({
      title: "Are you sure?",
      content: "Removing this domain will affect pages that are hosted on it.",
      cancelText: "Never Mind",
      cancelType: "primary",
      okText: "Delete",
      okType: "danger",
      maskClosable: true,
      onOk: () => {
        this.props.deleteDomain(this.props.urlsafe);
      },
    });
  };

  getStepFromStage = stage => {
    if (stage === 'dns' || stage === 'check-dns') {
      return 0;
    }
    if (stage === 'ssl') {
      return 1;
    }
    return 2;
  }

  getDnsInstructions = (url, stage) => {
    if (stage !== 'dns' && stage !== 'check-dns') {
      return null;
    }
    const actionNeeded = (
      <React.Fragment>
        <div className="captionLight">ACTION REQUIRED</div>
        <h4 className="mb-4">DNS configuration pending</h4>
      </React.Fragment>
    );
    const whyConfigure = (
      <p>
        You must first configure your domain's
        Name Settings (DNS) to use it with PageHits.
        You should be able to access these settings
        from your Domain Registrar - the company
        you bought your domain from.
      </p>
    );
    const checkPrompt = (
      <p>
        When done, tap the “Check configuration” button.
        Note that DNS changes may not take effect immediately.
        So if the check fails at first,
        wait for a couple of hours and check again.
      </p>
    );

    // if url does not match a valid domain
    const tldMatch = tldArray.find(e => url.endsWith(e));
    if (tldMatch === undefined) {
      return (
        <div>
          {actionNeeded}
          <p>
            <b>{url}</b>&nbsp;does not have a valid extension.
            If it was entered by mistake, simply remove it and try again.
            Else, follow the instructions below.
          </p>
          {whyConfigure}
          <p>
            Under your DNS settings, add either of these records:
          </p>
          <pre>
            Type: A<br />
            Value: {pageHitsIP}<br />
            TTL: 3600 seconds
          </pre>
          <p>
            OR
          </p>
          <pre>
            Type: CNAME<br />
            Key: insert subdomain<br />
            Value: {pageHitsAlias}<br />
            TTL: 3600 seconds
          </pre>
          {checkPrompt}
        </div>
      );
    }

    const tldStripped = url.substring(0, (url.length - tldMatch.length));

    const segments = tldStripped.split('.');
    if (segments.length === 1) {
      return (
        <div>
          {actionNeeded}
          {whyConfigure}
          <p>Under DNS settings, add this record:</p>
          <pre>
            Type: A<br />
            Value: {pageHitsIP}<br />
            TTL: 3600 seconds
          </pre>
          {checkPrompt}
        </div>
      );
    }

    segments.pop();
    const subdomain = segments.join('.');
    return (
      <div>
        {actionNeeded}
        {whyConfigure}
        <p>Under DNS settings, add this record:</p>
        <pre>
          Type: CNAME<br />
          Key: {subdomain}<br />
          Value: {pageHitsAlias}<br />
          TTL: 3600 seconds
        </pre>
        {checkPrompt}
      </div>
    );
  }

  render() {
    const {
      url, // the url
      stage, // added | confirmed
      status, // ui state idle -> loading -> idle
    } = this.props;

    // const isLoading = (status === 'loading');

    const urlNode = (
      <h5 className="text-white">{url}</h5>
    );

    let dnsStageBtn = null;
    if (status === 'not-reachable') {
      dnsStageBtn = (
        <Button type="primary" icon={<SyncOutlined />} onClick={this.onCheckDomainReachabilityBtnClick}>
          Check again
        </Button>
      );
    }
    else if (status === 'checking-reachability') {
      dnsStageBtn = (
        <Button type="primary" loading>Checking...</Button>
      );
    }
    else {
      dnsStageBtn = (
        <Button type="primary" icon={<SyncOutlined />} onClick={this.onCheckDomainReachabilityBtnClick}>
          Check configuration
        </Button>
      );
    }

    const dnsInstructions = this.getDnsInstructions(url, stage);

    const dnsStageNode = (
      <React.Fragment>
        <div>
          {dnsInstructions}
        </div>
        <div className="mt-4">
          {dnsStageBtn}
        </div>
      </React.Fragment>
    );

    const sslStageNode = (
      <div>
        <h5 className="mb-4">Almost there</h5>
        <p>
          Please stand by while we set up SSL certificates
          on your domain.
        </p>
      </div>
    );

    const stageSteps = (
      <Steps current={this.getStepFromStage(stage)}>
        <Step
          title="DNS setup"
          icon={(stage === 'check-dns') ? <LoadingOutlined /> : <SettingOutlined />}
        />
        <Step title="SSL setup" icon={<SafetyOutlined />} />
        <Step title="Ready" icon={<CheckCircleOutlined />} />
      </Steps>
    );

    const cardMenu = (
      <Menu onClick={({key})=>{
        if (key === "refresh") {
          this.props.refreshDomain(this.props.urlsafe);
        }
        else if (key === "delete") {
          this.removeAfterConfirm();
        }
      }}>
        <Menu.Item key="refresh">
          <SyncOutlined /> Refresh status
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="delete" className="text-danger">
          <DeleteOutlined /> Remove domain
        </Menu.Item>
      </Menu>
    );

    const cardOverflowButton = (
      <Dropdown overlay={cardMenu} placement="bottomLeft">
        <Button type="secondary" size="large" shape="circle" icon={<EllipsisOutlined />} />
      </Dropdown>
    );

    let currentNode = null;
    switch (stage) {
      case 'dns':
      case 'check-dns':
        currentNode = dnsStageNode;
        break;
      case 'ssl':
        currentNode = sslStageNode;
        break;
      default:
        currentNode = null;
    }

    return (
      <div className="container-fluid bg-white shadow rounded mb-4 overflow-hidden domain__item">
        <div className="row">
          <div className="col-12 pt-3 pb-2 px-3 bg-dark">
            {urlNode}
          </div>
        </div>
        <div className="row position-relative shift-up">
          <div className="row-shader d-none d-lg-block bg-dark" />
          <div className="col-12 col-lg-9 col-xl-8 col-xxl-6 order-2 order-lg-1">
            <div className="bg-white rounded px-lg-3 px-0 py-3 stageWrap">
              {stageSteps}
            </div>
          </div>
          <div className="col-12 col-lg-3 col-xl-2 col-xxl-3 order-1 order-lg-2 text-right text-lg-left">
            {cardOverflowButton}
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-9 col-xl-8 col-xxl-6 mt-4 mb-4 domain__detail">
            {currentNode}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/DomainItem.jsx